import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { type ClientCiblable, type Segment } from '@iobiz/shared'
import { ApiError, api } from '../../api/client.js'

/**
 * Le CHOIX NOMINATIF des destinataires.
 *
 * ⭐ Le segment répond à « quelle sorte de clientes », jamais à « celles-ci,
 * pas celle-là ». Or c'est le geste réel : on part d'un segment, puis on
 * décoche la cliente vue ce matin, celle qui a résilié, celle dont on sait
 * qu'elle n'aimera pas. Sans cette liste, la seule façon d'exclure quelqu'un
 * était de tordre les critères jusqu'à ce qu'il en sorte — en emportant dix
 * autres personnes au passage.
 *
 * ⚠️ `selection === null` veut dire « tout le segment », et un tableau VIDE
 * veut dire « personne ». Les deux ne se confondent jamais, ici comme au
 * serveur : c'est la confusion qui envoie une campagne à quatre cents
 * personnes qu'on venait d'exclure.
 */

interface Reponse {
  data: ClientCiblable[]
  total: number
  tronque: boolean
  plafond: number
}

export function ChoixDestinataires({
  storeId,
  segment,
  selection,
  onChange,
}: {
  storeId: string
  segment: Segment
  selection: string[] | null
  onChange: (selection: string[] | null) => void
}): JSX.Element {
  const [reponse, setReponse] = useState<Reponse | null>(null)
  const [chargement, setChargement] = useState(true)
  const [erreur, setErreur] = useState<string | null>(null)
  const [filtre, setFiltre] = useState('')

  const charger = useCallback(async (s: Segment): Promise<void> => {
    setChargement(true)
    setErreur(null)
    try {
      setReponse(await api.post<Reponse>(`/stores/${storeId}/campaigns/clients`, { segment: s }))
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Liste indisponible.')
      setReponse(null)
    } finally {
      setChargement(false)
    }
  }, [storeId])

  useEffect(() => {
    const t = setTimeout(() => void charger(segment), 400)
    return () => clearTimeout(t)
  }, [segment, charger])

  const clients = reponse?.data ?? []
  /** Seuls les joignables se cochent : cocher un client sans numéro
   *  promettrait un envoi qui ne peut pas avoir lieu. */
  const cochables = useMemo(() => clients.filter((c) => !c.empechement), [clients])

  const estCoche = useCallback(
    (id: string): boolean => (selection === null ? true : selection.includes(id)),
    [selection],
  )

  const retenus = selection === null ? cochables.length : selection.length

  function basculer(client: ClientCiblable): void {
    // Premier décochage : la sélection implicite « tous » se matérialise, sinon
    // il n'y aurait rien à retirer.
    const base = selection === null ? cochables.map((c) => c.id) : selection
    onChange(
      base.includes(client.id) ? base.filter((x) => x !== client.id) : [...base, client.id],
    )
  }

  const visibles = useMemo(() => {
    const q = filtre.trim().toLowerCase()
    if (!q) return clients
    return clients.filter(
      (c) =>
        c.nom.toLowerCase().includes(q) ||
        (c.ville ?? '').toLowerCase().includes(q) ||
        (c.phone ?? '').includes(q),
    )
  }, [clients, filtre])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {retenus} destinataire{retenus > 1 ? 's' : ''} retenu{retenus > 1 ? 's' : ''}
          {reponse && ` sur ${cochables.length} joignable${cochables.length > 1 ? 's' : ''}`}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={() => onChange(null)} disabled={selection === null}>
          Tout cocher
        </Button>
        <Button size="small" onClick={() => onChange([])} disabled={retenus === 0}>
          Tout décocher
        </Button>
      </Box>

      {erreur && <Alert severity="error">{erreur}</Alert>}

      {reponse?.tronque && (
        <Alert severity="warning">
          Ce segment ramène {reponse.total} clients ; seuls les {reponse.plafond} premiers sont
          affichés. Resserrez les critères : au-delà, la case à cocher n’est plus le bon outil.
        </Alert>
      )}

      {/* ⚠️ Tout décoché est un état LÉGITIME, pas une erreur de saisie — mais
          il empêche l'envoi, et il vaut mieux le dire ici qu'au lancement. */}
      {selection !== null && selection.length === 0 && (
        <Alert severity="warning">
          Aucun client coché : la campagne ne pourra pas partir en l’état.
        </Alert>
      )}

      <TextField
        size="small"
        placeholder="Filtrer par nom, ville, numéro…"
        value={filtre}
        onChange={(e) => setFiltre(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
      />

      <Paper variant="outlined" sx={{ borderRadius: 2, maxHeight: 300, overflowY: 'auto' }}>
        {chargement && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {!chargement && visibles.length === 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary', p: 4 }}>
            {clients.length === 0
              ? 'Ce segment ne ramène aucun client.'
              : 'Aucun client ne correspond à ce filtre.'}
          </Typography>
        )}

        {!chargement &&
          visibles.map((c) => (
            <Box
              key={c.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 2,
                py: 0.5,
                borderBottom: 1,
                borderColor: 'divider',
                '&:last-of-type': { borderBottom: 0 },
                opacity: c.empechement ? 0.55 : 1,
              }}
            >
              <Tooltip title={c.empechement ?? ''}>
                <span>
                  <Checkbox
                    size="small"
                    // ⭐ Un injoignable reste AFFICHÉ mais non cochable : le
                    // masquer ferait chercher pourquoi la fiche manque, au lieu
                    // de dire laquelle compléter.
                    disabled={Boolean(c.empechement)}
                    checked={!c.empechement && estCoche(c.id)}
                    onChange={() => basculer(c)}
                    inputProps={{ 'aria-label': `Cibler ${c.nom}` }}
                  />
                </span>
              </Tooltip>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ textDecoration: c.empechement ? 'line-through' : 'none' }}
                >
                  {c.nom}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {c.empechement ?? `${c.phone}${c.ville ? ` · ${c.ville}` : ''}`}
                </Typography>
              </Box>
            </Box>
          ))}
      </Paper>
    </Box>
  )
}
